import { useState } from 'react';
import {
  decimals,
  Field,
  Note,
} from '@wyocspay/ui';
import { money } from './format.js';
import { ApiError, api } from './api.js';

/**
 * Refund control.
 *
 * The copy here matters: after submitting, the payment does *not* immediately
 * read as refunded. The request goes to Stripe, and the status changes when
 * `charge.refunded` comes back verified. Telling the user that up front is
 * better than showing a stale status and letting them think it failed.
 */
export function RefundDialog({
  paymentId,
  amount,
  amountRefunded,
  currency,
  onDone,
  onCancel,
}: {
  paymentId: string;
  amount: number;
  amountRefunded: number;
  currency: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const remaining = amount - amountRefunded;
  const [full, setFull] = useState(true);
  const [partial, setPartial] = useState('');
  const [reason, setReason] = useState<'' | 'duplicate' | 'fraudulent' | 'requested_by_customer'>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ amount: number; remaining: number } | null>(null);

  // The currency's real minor-unit scale, not a hard-coded 100. A yen or won
  // amount has no decimal part, so multiplying by 100 turned 500 JPY into a
  // 50000 JPY refund - a hundredfold overpayment. `decimals` returns 0 for
  // those, 3 for the dinar currencies, 2 for everything else.
  const scale = 10 ** decimals(currency);
  const parsed = Math.round(Number.parseFloat(partial || '0') * scale);
  const amountToRefund = full ? remaining : parsed;
  const valid = full || (Number.isFinite(parsed) && parsed > 0 && parsed <= remaining);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.refund(paymentId, {
        amount: full ? undefined : amountToRefund,
        reason: reason || undefined,
      });
      setDone({ amount: result.refund.amount, remaining: result.refund.remaining });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <Note tone="ok" title={`${money(done.amount, currency)} refund submitted to Stripe`}>
          The payment status will change once Stripe confirms the refund - usually within seconds.
          It is not marked refunded here until that confirmation arrives.
          {done.remaining > 0 ? (
            <div style={{ marginTop: 6 }}>
              {money(done.remaining, currency)} would still be refundable after this settles.
            </div>
          ) : null}
        </Note>
        <button type="button" className="wc-btn" onClick={onDone}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          className={`wc-btn wc-btn-sm ${full ? '' : 'wc-btn-secondary'}`}
          onClick={() => setFull(true)}
        >
          Full - {money(remaining, currency)}
        </button>
        <button
          type="button"
          className={`wc-btn wc-btn-sm ${full ? 'wc-btn-secondary' : ''}`}
          onClick={() => setFull(false)}
        >
          Partial
        </button>
      </div>

      {!full ? (
        <Field
          label={`Amount (max ${money(remaining, currency)})`}
          hint={decimals(currency) === 0 ? 'Whole units, e.g. 500' : 'Entered in major units, e.g. 12.50'}
        >
          <input
            className="wc-input wc-num"
            inputMode="decimal"
            value={partial}
            onChange={(e) => setPartial(e.target.value)}
            placeholder="0.00"
            autoFocus
          />
        </Field>
      ) : null}

      <Field label="Reason (optional)" hint="Passed to Stripe; affects dispute defence.">
        <select className="wc-input" value={reason} onChange={(e) => setReason(e.target.value as typeof reason)}>
          <option value="">Not specified</option>
          <option value="requested_by_customer">Requested by customer</option>
          <option value="duplicate">Duplicate charge</option>
          <option value="fraudulent">Fraudulent</option>
        </select>
      </Field>

      {amountRefunded > 0 ? (
        <div style={{ marginBottom: 12 }}>
          <Note>
            {money(amountRefunded, currency)} of {money(amount, currency)} has already been refunded.
          </Note>
        </div>
      ) : null}

      {error ? (
        <div style={{ marginBottom: 12 }}>
          <Note tone="danger">{error}</Note>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="wc-btn wc-btn-danger"
          onClick={submit}
          disabled={busy || !valid}
          style={{ flex: 1 }}
        >
          {busy ? 'Submitting…' : `Refund ${valid ? money(amountToRefund, currency) : ''}`}
        </button>
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
